/* eslint-disable no-nested-ternary */
import { DISTRIBUTORS_HUB } from '@cannect/constants/products'
import api from '@cannect/services/api'
import { toast } from 'react-toastify'
import Banner from 'components/Banner'
import useMediaQuery from 'hooks/useMediaQuery'
import { usePrepareCart } from 'hooks/usePrepareCart'
import Button from 'new-components/Button'
import Heading from 'new-components/Heading'
import Modal from 'new-components/Modal'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FcApproval } from 'react-icons/fc'
import { useHistory } from 'react-router'
import { BRANDS_WITH_REQUIRED_FREIGHT, CLASS_WITHOUT_SHIPPING, MOBILE_WIDTH } from 'utils/constants'
import {
  formatDateBR,
  getCartSplitDeliveryEstimateTexts,
  orderHasPillItem,
  PILL_DELIVERY_CEP_TEXT
} from '@cannect/utils/deliveryEstimate'
import { isManualPriceItem } from '@cannect/utils/manualPrice'
import useQueryString from '../../hooks/useQueryString'
import { Address } from '../NewCartDocumets/Address'
import type { CustomerProps } from '../NewCartDocumets/Customer'
import { Customer } from '../NewCartDocumets/Customer'
import * as CustomerStyle from '../NewCartDocumets/Customer/FormCustomer/styles'
import { CustomerSchema } from '../NewCartDocumets/utils/customerSchema'
import { ProgressiveGroupSection } from '@cannect/new-components/ProgressiveGroupCard/ProgressiveGroupSection'
import { useUnavailableItems } from '@cannect/hooks/useUnavailableItems'
import UnavailableProductModal from '@cannect/new-components/UnavailableProductModal'
import Frete from './Frete'
import ProductList from './Product'
import { Resume } from './Resume'
import Step from './Steps'
import * as Style from './styles'
import { ContainerResume, ContentModal, SectionBlock, SectionTitle, WrapperModal } from './styles'

const formatEstimatedRangeText = (start?: string | null, end?: string | null) => {
  if (!start || !end) return null

  return `A entrega estimada é entre ${formatDateBR(start)} e ${formatDateBR(end)}.`
}

/**
 * Monta o payload de itens para o `PUT /order_cart(_public)`.
 *
 * O backend recria os itens via `generateItems`, que recalcula preço a partir de
 * `value_unitary`/`find_by_ocr` do PAYLOAD — não do banco. Omitir esses campos faz
 * o item com preço negociado pela marca (`changed_by_brand`) voltar ao preço de
 * tabela. Espelha a reconstrução feita no próprio backend (ordersController.js:14155).
 */
const buildItemsPayload = (items: any[]) =>
  (items || []).map((item: any) => ({
    product_id: item?.product_id || item?.product?.id || item?.id,
    quantity: item?.quantity || 1,
    ...(item?.changed_by_brand ? { value_unitary: item?.value_unitary ?? item?.unitPrice } : {}),
    ...(item?.find_by_ocr ? { find_by_ocr: item.find_by_ocr } : {}),
    product: item?.product || null
  }))

function PageCartProducts() {
  const isMobile = useMediaQuery(MOBILE_WIDTH)
  const {
    isUpdate,
    updateCart,
    lastOrderCreated,
    setOrderId,
    clearOrderData,
    validatingDeliveryCep,
    deliveryCep,
    setDeliveryCep,
    validateDeliveryCep
  } = usePrepareCart()
  const { filters } = useQueryString()
  const withFilter = filters?.cart_id
  const history = useHistory()
  const itens = lastOrderCreated?.items || []

  const previewItems = useMemo(
    () =>
      itens
        .filter((item: any) => !isManualPriceItem(item))
        .map((item: any) => ({
          product_id: Number(item?.product_id || item?.product?.id || item?.id),
          quantity: Number(item?.quantity || 1)
        }))
        .filter((i: { product_id: number; quantity: number }) => i.product_id > 0),
    [itens]
  )
  const {
    unavailableProducts,
    isUnavailableModalOpen,
    isRemoving,
    handleUnavailableError,
    closeModal: closeUnavailableModal
  } = useUnavailableItems()

  /**
   * Espelha os itens do carrinho para que o retry após a remoção automática de
   * produtos indisponíveis use a lista atualizada, e não o closure antigo.
   */
  const currentItemsRef = useRef<any[]>(itens)
  useEffect(() => {
    currentItemsRef.current = itens
  }, [itens])

  const [groupedProductIds, setGroupedProductIds] = useState<Set<number>>(new Set())
  const handleGroupedIdsChange = useCallback((ids: Set<number>) => setGroupedProductIds(ids), [])
  const noImportCost = lastOrderCreated?.noImportCost || false
  const isAllPill = itens.length > 0 && itens.every((item: any) => item?.product?.origin === 'pill')
  const isModalPayment = lastOrderCreated?.payments?.some((payment: any) => payment.status === 'paid')
  const [isModal, setIsModal] = useState(isModalPayment)
  /** Modal de prescrição: apenas fluxo white-label (carrinho-wa com cart_id) */
  const [isModalRecipeWarning, setIsModalRecipeWarning] = useState(false)
  const [openModalCupom, setCloseModalCupom] = useState(false)
  const [address, setAddress] = useState<{ shippingAddress: any; billingAddress: any }>({
    shippingAddress: {},
    billingAddress: {}
  })
  const [isAllAddress, setIsAllAddress] = useState(false)
  const [savingAddress, setSavingAddress] = useState(false)
  const [isAllCustomerData, setIsAllCustomerData] = useState(false)
  const [customer, setCustomer] = useState<CustomerProps>({} as CustomerProps)
  const [phone, setPhone] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const userId = lastOrderCreated?.person?.id
  const modalRecipeWarningKey = '@CANNECT:RECIPE_WARNING'

  const isAllSupplements = itens.every(
    (item: any) =>
      CLASS_WITHOUT_SHIPPING.includes(item?.product?.class_id || item?.class_id) &&
      !BRANDS_WITH_REQUIRED_FREIGHT.includes(item?.product?.brand_id || item?.brand_id)
  )

  const handleAddress = (type: 'shippingAddress' | 'billingAddress', data: any) => {
    setAddress((prev) => ({ ...prev, [type]: data }))
  }

  const isPhoneValid = CustomerSchema.shape.phone.safeParse(phone).success

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '')
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)})${cleaned.slice(2, 7)}-${cleaned.slice(7)}`
    }

    return cleaned
  }

  const handlePhone = (value: string) => {
    const onlyNumbers = value.replace(/\D/g, '').slice(0, 11)
    setPhone(onlyNumbers)

    const result = CustomerSchema.shape.phone.safeParse(onlyNumbers)
    setPhoneError(result.success ? '' : result.error.errors[0].message)
  }

  useEffect(() => {
    if (!withFilter) {
      const getOrders = async () => {
        const { data } =
          !userId || !withFilter ? await api.get(`/order_last_paid`) : await api.get(`/order_last_paid/${userId}`)
        if (Object.keys(data.order).length === 0) {
          setCloseModalCupom(true)
        }
      }
      getOrders()
    }
  }, [])

  useEffect(() => {
    if (isModalPayment) {
      localStorage.removeItem('@CANNECT:ORDER_CHECKOUT_CART_ID')
      setOrderId(null)
      clearOrderData()
    }
  })

  useEffect(() => {
    if (lastOrderCreated?.person) {
      setCustomer(lastOrderCreated.person as CustomerProps)
    }
  }, [lastOrderCreated?.person])

  useEffect(() => {
    if (lastOrderCreated?.person?.phone) {
      handlePhone(String(lastOrderCreated.person.phone))
    }
  }, [lastOrderCreated?.person?.phone])

  const validatedCepRef = useRef<string>('')

  // Seed CEP from order when loading /carrinho-wa (cart_id flow)
  useEffect(() => {
    if (!withFilter) return
    const digits = String(lastOrderCreated?.cep || '').replace(/\D/g, '')
    if (digits.length !== 8) return
    setDeliveryCep(digits)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withFilter, lastOrderCreated?.cep])

  // Validate delivery whenever CEP reaches 8 digits (auto-seed or user-typed CEP)
  useEffect(() => {
    if (!withFilter) return
    const digits = (deliveryCep || '').replace(/\D/g, '')
    if (digits.length !== 8) return
    if (validatedCepRef.current === deliveryCep) return
    validatedCepRef.current = deliveryCep
    validateDeliveryCep()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryCep, withFilter])

  const containsProductWithLongerDeliveryTime = itens.some(
    (item: any) => item?.product?.stock === 0 && DISTRIBUTORS_HUB.includes(item?.product?.distributor_id)
  )
  const containsProductWithHubDistributor = itens.some((item: any) =>
    DISTRIBUTORS_HUB.includes(item?.product?.distributor_id)
  )
  const containProductsMatirz = itens.some((item: any) => !DISTRIBUTORS_HUB.includes(item?.product?.distributor_id))
  const estimatedRangeText = formatEstimatedRangeText(
    lastOrderCreated?.estimated_delivery_range_start,
    lastOrderCreated?.estimated_delivery_range_end
  )
  const splitEstimateTexts = getCartSplitDeliveryEstimateTexts(lastOrderCreated)
  const hasPillItem = orderHasPillItem(lastOrderCreated)
  const shippingRestriction =
    lastOrderCreated?.shipping_restriction ?? lastOrderCreated?.summary?.shipping_restriction ?? null
  const isDeliveryCovered = shippingRestriction === null || shippingRestriction?.covered !== false

  useEffect(() => {
    if (!withFilter) return

    const oneHourInMilli = 60 * 60 * 1000
    const actualDateInMilli = Date.now()
    const dismissedAtRaw = localStorage.getItem(modalRecipeWarningKey)
    const dismissedAt = dismissedAtRaw ? parseInt(dismissedAtRaw.replace(/"/g, ''), 10) : NaN

    if (!isAllSupplements && !isAllPill) {
      if (!dismissedAtRaw) {
        setIsModalRecipeWarning(true)
      } else if (Number.isFinite(dismissedAt) && actualDateInMilli >= dismissedAt + oneHourInMilli) {
        localStorage.removeItem(modalRecipeWarningKey)
        setIsModalRecipeWarning(true)
      }
    }
  }, [withFilter, isAllSupplements, modalRecipeWarningKey])

  const closeModalRecipeWarning = () => {
    const actualDateInMilli = Date.now()
    setIsModalRecipeWarning(false)
    localStorage.setItem(modalRecipeWarningKey, String(actualDateInMilli))
  }

  const closeModal = () => {
    setIsModal(false)
    if (withFilter) {
      history.push(`/analise-cannect-wa?cart_id=${withFilter}`)
    } else {
      history.push(`/analise-cannect`)
    }
  }

  const redirect = async () => {
    if (withFilter) {
      const hasAddress = address?.shippingAddress && Object.keys(address.shippingAddress).length > 0
      if (hasAddress && lastOrderCreated?.id) {
        setSavingAddress(true)
        try {
          const { data } = await api.put(`/order_cart_public/${lastOrderCreated.id}`, {
            shippingAddress: address.shippingAddress,
            billingAddress: address.billingAddress || address.shippingAddress,
            person_id: lastOrderCreated?.person?.id,
            phone,
            // Enviado para que o backend rode o guard de disponibilidade e
            // responda 422 já nesta tela, e não só no checkout.
            items: buildItemsPayload(currentItemsRef.current)
          })
          if (data?.order?.id) {
            history.push(`/finalizar-compra-wa/${data.order.id}?cart_id=${withFilter}`)
          } else {
            history.push(`/finalizar-compra-wa/${lastOrderCreated.id}?cart_id=${withFilter}`)
          }
        } catch (e) {
          const handled = await handleUnavailableError(e)
          if (!handled) toast.error('Erro ao salvar endereço. Tente novamente.')
        } finally {
          setSavingAddress(false)
        }
      } else {
        history.push(`/finalizar-compra-wa/${lastOrderCreated?.id}?cart_id=${withFilter}`)
      }
    } else {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          allow_custom_scripts: true,
          send_to: 'DC-15596039/invmedia/canne004+standard'
        })
      }

      setSavingAddress(true)
      try {
        // Persistir endereço e dados pessoais antes de continuar (como na etapa antiga)
        const BASE_PAYMENT_URL = withFilter ? 'order_cart_public' : 'order_cart'
        const payload = {
          shippingAddress: address.shippingAddress,
          billingAddress: address.billingAddress || address.shippingAddress,
          items: buildItemsPayload(currentItemsRef.current),
          customer,
          person_id: withFilter ? lastOrderCreated?.person?.id : undefined
        }
        await api.put(`/${BASE_PAYMENT_URL}/${lastOrderCreated?.id}`, payload)

        const target = lastOrderCreated?.id
          ? `/finalizar-compra-wa/${lastOrderCreated.id}${withFilter ? `?cart_id=${withFilter}` : ''}`
          : withFilter
            ? `/finalizar-compra-wa/${withFilter}`
            : '/finalizar-compra-wa'
        history.push(target)
      } catch (e: any) {
        const handled = await handleUnavailableError(e)
        if (!handled) toast.error(e?.response?.data?.message || 'Erro ao salvar endereço/dados pessoais')
      } finally {
        setSavingAddress(false)
      }
    }
  }

  const goToProducts = () => {
    history.push(`/produtos/1`)
  }

  /** Botão 3 do modal: descarta o aviso e refaz o fluxo com o carrinho já sem os itens removidos. */
  const handleContinueAfterRemoval = () => {
    closeUnavailableModal()
    redirect()
  }

  const handleNextCart = () => {}

  return (
    <Style.Container isMobile={isMobile} isCarrinhoWa={!!withFilter}>
      <Step isCarrinhoWa={!!withFilter} />
      {withFilter && <SectionTitle style={{ marginBottom: 16 }}>Produtos</SectionTitle>}
      <ProductList isCarrinhoWa={!!withFilter} excludeIds={groupedProductIds} />
      <ProgressiveGroupSection cartItems={previewItems} onGroupedIdsChange={handleGroupedIdsChange} />
      {!isAllSupplements && !withFilter && (
        <>
          <Banner title="ATENÇÃO" variant="info">
            <p>
              Confira se o produto e a quantidade inseridos correspondem à sua prescrição médica. Você pode realizar uma
              compra parcial, mas não deve ultrapassar o limite máximo indicado na receita. Em caso de dúvidas, consulte
              seu médico.
            </p>
          </Banner>
          <Banner title="IMPORTANTE" variant="warning">
            {validatingDeliveryCep ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="delivery-estimate-spinner" />
                <p>Calculando estimativa de entrega...</p>
              </div>
            ) : splitEstimateTexts ? (
              splitEstimateTexts.map((text) => <p key={text}>{text}</p>)
            ) : estimatedRangeText ? (
              <p>{estimatedRangeText}</p>
            ) : hasPillItem ? (
              <p>{PILL_DELIVERY_CEP_TEXT}</p>
            ) : containProductsMatirz ? (
              <p>O prazo de entrega é de 25 dias úteis a partir da confirmação do pagamento.</p>
            ) : containsProductWithHubDistributor ? (
              <p>O prazo de entrega é de 15 dias úteis a partir da confirmação do pagamento.</p>
            ) : null}
          </Banner>
        </>
      )}
      {!isAllSupplements && withFilter && (
        <Banner title="IMPORTANTE" variant="warning">
          {validatingDeliveryCep ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="delivery-estimate-spinner" />
              <p>Calculando estimativa de entrega...</p>
            </div>
          ) : splitEstimateTexts ? (
            splitEstimateTexts.map((text) => <p key={text}>{text}</p>)
          ) : estimatedRangeText ? (
            <p>{estimatedRangeText}</p>
          ) : hasPillItem ? (
            <p>{PILL_DELIVERY_CEP_TEXT}</p>
          ) : containProductsMatirz ? (
            <p>O prazo de entrega é de 25 dias úteis a partir da confirmação do pagamento.</p>
          ) : containsProductWithHubDistributor ? (
            <p>O prazo de entrega é de 15 dias úteis a partir da confirmação do pagamento.</p>
          ) : null}
        </Banner>
      )}

      {withFilter && shippingRestriction?.covered === false && (
        <Banner title="ENTREGA INDISPONÍVEL" variant="negative">
          <p>Infelizmente não conseguimos realizar a entrega para o CEP informado.</p>
          {Array.isArray(shippingRestriction?.blocked_products) && shippingRestriction.blocked_products.length > 0 && (
            <ul style={{ marginTop: 4, paddingLeft: 16 }}>
              {shippingRestriction.blocked_products.map((p: any) => (
                <li key={p.product_id}>{p.name}</li>
              ))}
            </ul>
          )}
        </Banner>
      )}

      {withFilter ? (
        <>
          <SectionBlock>
            <SectionTitle>Endereço</SectionTitle>
            <Address
              setIsAllAddress={setIsAllAddress}
              handleAddress={handleAddress}
              hasAssociationProducts={lastOrderCreated?.deliveryByAssociation ?? false}
            />
          </SectionBlock>

          <SectionBlock>
            <SectionTitle>Contato</SectionTitle>
            <CustomerStyle.Container isMobile={isMobile}>
              <CustomerStyle.FormContainer isMobile={isMobile}>
                <div className="input-groups">
                  <div>
                    <span>Celular *</span>
                    <input
                      type="text"
                      id="phone"
                      required
                      value={formatPhoneNumber(phone)}
                      onChange={(event) => handlePhone(event.target.value)}
                    />
                    {phoneError && <CustomerStyle.ErrorMessage>{phoneError}</CustomerStyle.ErrorMessage>}
                  </div>
                </div>
              </CustomerStyle.FormContainer>
            </CustomerStyle.Container>
          </SectionBlock>
        </>
      ) : (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Address setIsAllAddress={setIsAllAddress} handleAddress={handleAddress} />
          <Customer customer={customer} setCostumer={setCustomer} setIsAllCustomerData={setIsAllCustomerData} />
        </div>
      )}

      <ContainerResume isMobile={isMobile}>
        {lastOrderCreated?.origin !== 'REENVIO' && !noImportCost && !isAllPill && <Frete />}
        <Resume hideCoupon={!!withFilter} />
      </ContainerResume>

      <Style.ButtonContainer>
        <Style.ButtonContinue
          onClick={() => redirect()}
          disabled={
            isUpdate ||
            itens.length <= 0 ||
            savingAddress ||
            (withFilter && (!isAllAddress || !isPhoneValid || !isDeliveryCovered)) ||
            (!withFilter && (!isAllAddress || !isAllCustomerData))
          }>
          {isUpdate ? 'Atualizando...' : savingAddress ? 'Salvando...' : 'Continuar'}
        </Style.ButtonContinue>
      </Style.ButtonContainer>
      <Modal isOpen={isModal} setOpenModal={closeModal}>
        <WrapperModal>
          <FcApproval size={40} />
          <Heading elementType="h2" defaultColor="darkGrey" fontSize="20px" verticalSpace="small">
            Obrigado!
          </Heading>
          <ContentModal>
            <p>O seu pedido já foi processado e pago com sucesso.</p>
            <Button onClick={() => goToProducts()}>Buscar novos produtos</Button>
            <Button onClick={() => closeModal()}>Ver detalhes do pedido finalizado</Button>
          </ContentModal>
        </WrapperModal>
      </Modal>
      <UnavailableProductModal
        isOpen={isUnavailableModalOpen}
        products={unavailableProducts}
        isLoading={isRemoving}
        onClose={closeUnavailableModal}
        onContinue={handleContinueAfterRemoval}
      />
      {withFilter && (
        <Modal isOpen={isModalRecipeWarning && !isModal} setOpenModal={closeModalRecipeWarning}>
          <WrapperModal>
            <Heading elementType="h2" defaultColor="darkGrey" fontSize="20px" verticalSpace="small">
              ATENÇÃO
            </Heading>
            <ContentModal>
              <p>
                Para finalizar o seu processo de importação, é necessário possuir uma prescrição médica emitida há menos
                de 5 meses. Caso não possua uma prescrição válida,{' '}
                <a
                  style={{ color: 'rgb(107, 123, 60)', textDecoration: 'underline' }}
                  href="https://api.whatsapp.com/send/?phone=5511982711992&text=Ol%C3%A1%2C+gostaria+de+agendar+uma+consulta+com+especialista+em+cannabis+medicinal%21+-+Inicie+a+conversa+para+ser+atendido&type=phone_number&app_absent=0">
                  clique aqui
                </a>{' '}
                para falar conosco pelo WhatsApp
              </p>
              <Button onClick={() => closeModalRecipeWarning()}>CONTINUAR</Button>
            </ContentModal>
          </WrapperModal>
        </Modal>
      )}
    </Style.Container>
  )
}

export default PageCartProducts
